import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  LiveKitRoom,
  useTracks,
  ControlBar,
  useDataChannel,
  RoomAudioRenderer,
  useLocalParticipant,
  useParticipants,
} from "@livekit/components-react";
import { Track, VideoPresets } from "livekit-client";
import "@livekit/components-styles";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Radio, Loader2, X, Heart, MessageSquare, Share2, Copy, Video, VideoOff, Mic, MicOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import LiveChatPanel from "@/components/plyx/LiveChatPanel";
import MiniHourButton from "@/components/plyx/MiniHourButton";
import StreamGoalWidget from "@/components/plyx/StreamGoalWidget";
import PlyxStagePanel from "@/components/plyx/PlyxStagePanel";
import GiftDrawer from "@/components/plyx/GiftDrawer";
import BumperBattlePanel from "@/components/plyx/BumperBattlePanel";
import { Gift } from "lucide-react";
import { primePlyxMediaPermissions, plyxMediaPermissionMessage } from "@/lib/plyxMediaPermissions";

/**
 * Plyx Go Live page — `/plyx/live/new`
 *
 * Flow:
 *   1. Auth-gated. Creator types a title.
 *   2. POST /plyx/live/start → backend creates room + mints publisher token.
 *   3. <LiveKitRoom> connects with the token; viewers can drop in via the
 *      shareable URL printed under the broadcaster preview.
 *   4. "End stream" calls POST /plyx/live/{room}/end and navigates away.
 */
/**
 * Render a SINGLE LiveKit video track inside a plain <video> element.
 * Hand-rolling this lets us bypass GridLayout / ParticipantTile, which
 * (per the user's iter-98 bug report) sometimes failed to surface the
 * LOCAL participant's camera even when `localParticipant.isCameraEnabled`
 * was true. Attaching the track manually is bullet-proof.
 *
 * `mirror` flips the video horizontally for selfie-style preview.
 */
const PlyxRawVideo = ({ track, mirror = false, label, isLocal = false }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    if (!track || !videoRef.current) return;
    try {
      track.attach(videoRef.current);
    } catch { /* ignore */ }
    return () => {
      try { track.detach(videoRef.current); } catch { /* ignore */ }
    };
  }, [track]);
  return (
    <div className="relative w-full h-full bg-black overflow-hidden">
      {track ? (
        <video
          ref={videoRef}
          autoPlay
          muted={isLocal}
          playsInline
          className="w-full h-full object-cover"
          style={mirror ? { transform: "scaleX(-1)" } : undefined}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">
          Camera off
        </div>
      )}
      {label && (
        <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-semibold">
          {label}
        </div>
      )}
    </div>
  );
};

const BroadcasterStage = ({ roomName }) => {
  const { localParticipant } = useLocalParticipant();
  const participants = useParticipants();
  // Force a re-render whenever local/remote tracks change so we pick
  // up the camera publication right after `setCameraEnabled(true)`.
  const [, force] = useState(0);
  useEffect(() => {
    if (!localParticipant) return;
    const refresh = () => force((v) => v + 1);
    localParticipant.on("trackPublished", refresh);
    localParticipant.on("trackUnpublished", refresh);
    localParticipant.on("localTrackPublished", refresh);
    localParticipant.on("localTrackUnpublished", refresh);
    localParticipant.on("trackMuted", refresh);
    localParticipant.on("trackUnmuted", refresh);
    return () => {
      localParticipant.off("trackPublished", refresh);
      localParticipant.off("trackUnpublished", refresh);
      localParticipant.off("localTrackPublished", refresh);
      localParticipant.off("localTrackUnpublished", refresh);
      localParticipant.off("trackMuted", refresh);
      localParticipant.off("trackUnmuted", refresh);
    };
  }, [localParticipant]);

  // Gather every participant (local first) and pull their Camera
  // publication. Filter out muted / track-less rows so we don't render
  // a black box per missing publisher.
  const tiles = [];
  if (localParticipant) {
    const pub = localParticipant.getTrackPublication?.(Track.Source.Camera);
    tiles.push({
      key: localParticipant.identity || "local",
      track: pub && !pub.isMuted ? pub.track : null,
      label: "You",
      mirror: true,
      isLocal: true,
    });
  }
  for (const p of participants) {
    if (!p || p.identity === localParticipant?.identity || p.isLocal) continue;
    const pub = p.getTrackPublication?.(Track.Source.Camera);
    tiles.push({
      key: p.identity,
      track: pub && !pub.isMuted ? pub.track : null,
      label: p.name || `@${p.identity}`,
      mirror: false,
      isLocal: false,
    });
  }

  // Layout: single tile fills the screen; 2+ tiles split into a grid.
  const cols = tiles.length <= 1 ? 1 : tiles.length <= 4 ? 2 : 3;
  return (
    <div
      className="w-full h-full grid gap-1 p-1 bg-black"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      data-testid="plyx-live-broadcaster-stage"
      data-room={roomName}
    >
      {tiles.map((t) => (
        <PlyxRawVideo key={t.key} track={t.track} mirror={t.mirror} label={t.label} isLocal={t.isLocal} />
      ))}
    </div>
  );
};

/**
 * Big, obvious host-side camera + mic toggles + an "ask guest to share
 * camera" data-channel ping. Lives INSIDE the LiveKitRoom so we can
 * reach the localParticipant via the hook.
 *
 * Why this exists: the LiveKit `<ControlBar>` is tiny, easy to miss,
 * and on iOS Safari it can't recover when the auto-publish silently
 * fails. These big floating buttons let the host re-prompt the
 * browser for permission at any time, plus retry-publish their own
 * tracks. They also keep a count of guests whose camera isn't yet
 * publishing so the host can nudge them with one tap.
 */
const BroadcasterControls = () => {
  const { localParticipant } = useLocalParticipant();
  const [camOn, setCamOn] = React.useState(false);
  const [micOn, setMicOn] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  // Track remote camera/mic publication count so we know if any guest
  // has their camera off — then the nudge button surfaces.
  const remoteTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: false }],
    { onlySubscribed: false },
  );
  // Anyone on stage WITHOUT a published video track (host excluded).
  const guestsWithoutCamera = React.useMemo(() => {
    if (!localParticipant) return [];
    // remoteParticipants is available on the underlying room object via
    // the data channel hook below. Fall back gracefully.
    const room = localParticipant?.room;
    if (!room) return [];
    const out = [];
    room.remoteParticipants?.forEach?.((p) => {
      const hasVid = Array.from(p.videoTrackPublications.values()).some(
        (pub) => pub.track && !pub.isMuted,
      );
      if (!hasVid) out.push({ identity: p.identity, name: p.name || p.identity });
    });
    return out;
  }, [localParticipant, remoteTracks]);

  // Keep our local toggle state in sync with what LiveKit actually
  // published (in case auto-publish on connect succeeded or failed).
  React.useEffect(() => {
    if (!localParticipant) return;
    const sync = () => {
      setCamOn(localParticipant.isCameraEnabled);
      setMicOn(localParticipant.isMicrophoneEnabled);
    };
    sync();
    const handler = () => sync();
    localParticipant.on("trackMuted", handler);
    localParticipant.on("trackUnmuted", handler);
    localParticipant.on("trackPublished", handler);
    localParticipant.on("trackUnpublished", handler);
    return () => {
      localParticipant.off("trackMuted", handler);
      localParticipant.off("trackUnmuted", handler);
      localParticipant.off("trackPublished", handler);
      localParticipant.off("trackUnpublished", handler);
    };
  }, [localParticipant]);

  const toggleCam = async () => {
    if (!localParticipant || busy) return;
    setBusy(true);
    try {
      const next = !localParticipant.isCameraEnabled;
      // If we're turning it ON and the browser may never have prompted
      // for camera (e.g. user denied earlier), re-prime first.
      if (next) {
        const perm = await primePlyxMediaPermissions({ video: true, audio: false });
        if (!perm.ok) {
          toast.error(plyxMediaPermissionMessage(perm.error));
          return;
        }
      }
      await localParticipant.setCameraEnabled(next);
      setCamOn(next);
    } catch (e) {
      toast.error("Couldn't toggle camera — refresh and try again");
    } finally {
      setBusy(false);
    }
  };

  const toggleMic = async () => {
    if (!localParticipant || busy) return;
    setBusy(true);
    try {
      const next = !localParticipant.isMicrophoneEnabled;
      if (next) {
        const perm = await primePlyxMediaPermissions({ video: false, audio: true });
        if (!perm.ok) {
          toast.error(plyxMediaPermissionMessage(perm.error));
          return;
        }
      }
      await localParticipant.setMicrophoneEnabled(next);
      setMicOn(next);
    } catch (e) {
      toast.error("Couldn't toggle mic — refresh and try again");
    } finally {
      setBusy(false);
    }
  };

  // Nudge guests whose camera isn't publishing — sends a data-channel
  // ping; their PlyxLiveViewer listens for `host_request_camera` and
  // re-runs the camera-on flow (which itself primes permissions inside
  // a fresh user gesture once they tap the toast Accept).
  const nudgeGuestCameras = async () => {
    if (!localParticipant) return;
    try {
      const payload = JSON.stringify({ type: "host_request_camera" });
      const data = new TextEncoder().encode(payload);
      await localParticipant.publishData(data, { reliable: true, topic: "plyx-stage" });
      toast.success("Asked your guest(s) to turn on their camera");
    } catch (e) {
      toast.error("Couldn't reach the guest — try again");
    }
  };

  return (
    <div
      data-testid="plyx-broadcaster-controls"
      className="absolute top-3 left-1/2 -translate-x-1/2 z-[55] flex items-center gap-2 px-3 py-2 rounded-full bg-black/70 backdrop-blur-md border border-white/15 shadow-xl"
    >
      <button
        type="button"
        onClick={toggleCam}
        disabled={busy}
        data-testid="plyx-host-cam-toggle"
        title={camOn ? "Turn camera off" : "Turn camera on"}
        aria-label={camOn ? "Turn camera off" : "Turn camera on"}
        className={
          "inline-flex items-center justify-center w-11 h-11 rounded-full transition disabled:opacity-50 "
          + (camOn ? "bg-white text-black" : "bg-[#FF3D7F] text-white animate-pulse")
        }
      >
        {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
      </button>
      <button
        type="button"
        onClick={toggleMic}
        disabled={busy}
        data-testid="plyx-host-mic-toggle"
        title={micOn ? "Mute" : "Unmute"}
        aria-label={micOn ? "Mute" : "Unmute"}
        className={
          "inline-flex items-center justify-center w-11 h-11 rounded-full transition disabled:opacity-50 "
          + (micOn ? "bg-white text-black" : "bg-[#FF3D7F] text-white animate-pulse")
        }
      >
        {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
      </button>
      {guestsWithoutCamera.length > 0 && (
        <button
          type="button"
          onClick={nudgeGuestCameras}
          data-testid="plyx-host-nudge-cam"
          title={`Ask ${guestsWithoutCamera.length} guest${guestsWithoutCamera.length === 1 ? "" : "s"} to turn on camera`}
          className="inline-flex items-center gap-1.5 px-3 h-11 rounded-full bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black font-bold text-xs"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Ask camera on
        </button>
      )}
    </div>
  );
};

/**
 * Host-side "Send a gift" launcher. Always visible while broadcasting.
 *
 * When the host taps it:
 *   • If there are no guests on stage → drawer opens with NO target,
 *     which (per backend rules) still errors "can't gift yourself" —
 *     so we short-circuit with a friendly toast.
 *   • If exactly ONE guest is on stage → opens the drawer pre-targeted
 *     to that guest (most common case for 1-on-1 streams).
 *   • If multiple guests → pop a tiny picker first; the chosen guest
 *     becomes the gift recipient.
 *
 * Backend (`POST /plyx/live/:room/gift` and `/tip`) accepts an
 * optional `target_user_id` that, when set by the host and pointing to
 * an on-stage guest, redirects the credit + GBP payout to that guest
 * instead of the room creator.
 */
const HostGiftLauncher = ({ roomName }) => {
  const { localParticipant } = useLocalParticipant();
  const participants = useParticipants();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [target, setTarget] = useState(null); // {id, label}
  const [pickerOpen, setPickerOpen] = useState(false);

  // Stage guests = remote participants other than the host. Use
  // identity as plyx_user_id (the live-token mint uses plyx_user_id).
  const stageGuests = participants.filter(
    (p) => p && p.identity && (!localParticipant || p.identity !== localParticipant.identity),
  );

  const openGiftFlow = () => {
    if (stageGuests.length === 0) {
      toast.message("Invite a guest on stage first — you'll be able to gift them then.", { duration: 4000 });
      return;
    }
    if (stageGuests.length === 1) {
      const g = stageGuests[0];
      setTarget({ id: g.identity, label: g.name || `@${g.identity.slice(0, 10)}` });
      setDrawerOpen(true);
      return;
    }
    setPickerOpen(true);
  };

  const pickGuest = (g) => {
    setTarget({ id: g.identity, label: g.name || `@${g.identity.slice(0, 10)}` });
    setPickerOpen(false);
    setDrawerOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={openGiftFlow}
        data-testid="plyx-host-gift-btn"
        title="Send a gift to a guest"
        className="absolute top-3 right-3 z-[55] inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-black font-bold text-xs shadow-xl"
      >
        <Gift className="w-4 h-4" />
        Send a gift
      </button>
      {pickerOpen && (
        <div
          className="absolute top-14 right-3 z-40 w-56 rounded-2xl bg-[#13102a] text-white border border-white/15 shadow-2xl p-3"
          data-testid="plyx-host-gift-picker"
        >
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300 mb-2">Gift who?</div>
          <div className="flex flex-col gap-1">
            {stageGuests.map((g) => (
              <button
                key={g.identity}
                type="button"
                onClick={() => pickGuest(g)}
                data-testid={`plyx-host-gift-target-${g.identity}`}
                className="text-left px-3 py-2 rounded-lg hover:bg-white/10 text-sm"
              >
                {g.name || `@${g.identity.slice(0, 12)}`}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setPickerOpen(false)}
            className="mt-2 w-full px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-[11px] text-white/60"
          >
            Cancel
          </button>
        </div>
      )}
      <GiftDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setTarget(null); }}
        liveRoomName={roomName}
        targetUserId={target?.id}
        targetLabel={target?.label}
      />
    </>
  );
};

/** Listen for viewer hearts + gift bursts and animate them on the
 *  broadcaster's screen. Lives inside <LiveKitRoom>. */
const BroadcasterEngagement = ({ roomName }) => {
  const [hearts, setHearts] = useState([]);
  const [bursts, setBursts] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [goalEcho, setGoalEcho] = useState(null);
  const idRef = React.useRef(0);
  const { message } = useDataChannel("plyx-engagement");

  useEffect(() => {
    if (!message) return;
    try {
      const txt = new TextDecoder().decode(message.payload);
      const msg = JSON.parse(txt);
      idRef.current += 1;
      const id = idRef.current;
      if (msg.type === "heart") {
        const x = (Math.random() - 0.5) * 80;
        setHearts((p) => [...p, { id, x }].slice(-40));
        setTimeout(() => setHearts((p) => p.filter((h) => h.id !== id)), 3000);
      } else if (msg.type === "gift" && msg.emoji) {
        setBursts((p) => [...p, { id, emoji: msg.emoji }].slice(-20));
        setTimeout(() => setBursts((p) => p.filter((b) => b.id !== id)), 3500);
      } else if (msg.type === "goal_update" && msg.goal) {
        setGoalEcho(msg.goal);
      }
    } catch {/* ignore */}
  }, [message]);

  return (
    <>
      <div className="pointer-events-none absolute bottom-24 right-12 z-20">
        {hearts.map((h) => (
          <Heart
            key={h.id}
            className="absolute right-0 bottom-0 w-7 h-7 text-pink-500 fill-pink-500"
            style={{ animation: "plyxHeartFly 3s ease-out forwards", left: `${h.x}px` }}
          />
        ))}
      </div>
      <div className="pointer-events-none absolute bottom-24 left-1/2 -translate-x-1/2 z-20">
        {bursts.map((b, i) => (
          <span
            key={b.id}
            className="absolute text-5xl"
            style={{ animation: "plyxGiftRise 3.5s ease-out forwards", left: `${(i % 5) * 28 - 60}px`, bottom: 0 }}
          >
            {b.emoji}
          </span>
        ))}
      </div>

      {/* Floating chat toggle (broadcaster's own moderation view) */}
      <button
        type="button"
        data-testid="plyx-live-broadcaster-chat-toggle"
        onClick={() => setChatOpen((v) => !v)}
        className="absolute top-4 right-4 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-bold backdrop-blur"
      >
        <MessageSquare className="w-3.5 h-3.5" /> Chat
      </button>

      <LiveChatPanel roomName={roomName} open={chatOpen} onClose={() => setChatOpen(false)} isMod={true} />

      {/* Mini Live Hour — one-tap 5-minute 2× boost (broadcaster-only)
          Moved to the TOP-LEFT so it never sits on top of the LiveKit
          ControlBar at the bottom — that bar holds the camera flip /
          mute toggles and was being covered by this button. */}
      <div className="absolute top-4 left-4 z-30 pointer-events-auto">
        <MiniHourButton roomName={roomName} />
      </div>

      {/* Stream goal — broadcaster mode (Set goal button shows here) */}
      <StreamGoalWidget roomName={roomName} isBroadcaster={true} liveProgress={goalEcho} />

      <style>{`
        @keyframes plyxHeartFly {
          0% { transform: translateY(0) scale(0.7); opacity: 0; }
          15% { transform: translateY(-20px) scale(1); opacity: 1; }
          100% { transform: translateY(-360px) scale(1.4) rotate(20deg); opacity: 0; }
        }
        @keyframes plyxGiftRise {
          0% { transform: translateY(0) scale(0.6); opacity: 0; }
          15% { transform: translateY(-30px) scale(1.2); opacity: 1; }
          70% { transform: translateY(-260px) scale(1.5); opacity: 1; }
          100% { transform: translateY(-380px) scale(1.7) rotate(-15deg); opacity: 0; }
        }
      `}</style>
    </>
  );
};

const PlyxLiveNew = () => {
  const { user, loading: authLoading } = usePlyxAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [starting, setStarting] = useState(false);
  const [session, setSession] = useState(null); // { token, room_name, livekit_url }
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/plyx/login", { replace: true, state: { from: "/plyx/live/new" } });
    }
  }, [user, authLoading, navigate]);

  const handleGoLive = async () => {
    if (!title.trim()) {
      toast.error("Give your stream a title");
      return;
    }
    // CRITICAL — iOS Safari only fires the camera + mic permission
    // prompt when getUserMedia is invoked SYNCHRONOUSLY inside a user
    // gesture. The subsequent async API call breaks that gesture
    // chain, so we have to prime permissions HERE — first thing on
    // tap — before anything else awaits. The helper stops the test
    // tracks immediately so LiveKit can mount its own with the grant
    // we just secured.
    setStarting(true);
    const perm = await primePlyxMediaPermissions({ video: true, audio: true });
    if (!perm.ok) {
      toast.error(plyxMediaPermissionMessage(perm.error));
      setStarting(false);
      return;
    }
    try {
      const r = await api.post("/plyx/live/start", { title: title.trim() });
      setSession(r.data);
      toast.success("You're live! Share your link.");
      // Reassure the broadcaster that the bell ping went out to their
      // crew — the backend fans live.start notifs to followers AND
      // followings automatically. Without this hint the creator has no
      // visible confirmation that the network was pinged.
      setTimeout(() => {
        toast.message("📣 Notified your followers & people you follow", { duration: 5000 });
      }, 1200);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not start your stream";
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  };

  const handleEnd = useCallback(async () => {
    if (!session?.room_name) return;
    setEnding(true);
    try {
      await api.post(`/plyx/live/${session.room_name}/end`);
    } catch (e) {
      // ignore — UI navigates away anyway
    }
    setSession(null);
    toast.message("Stream ended");
    navigate("/plyx/live");
  }, [session, navigate]);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pb-20">
      {!session ? (
        <div className="max-w-md mx-auto px-4 pt-10">
          <h1 className="text-4xl font-black bg-gradient-to-r from-[#FF3D7F] to-[#FFCC2E] bg-clip-text text-transparent">
            Go Live
          </h1>
          <p className="text-white/60 text-sm mt-2">
            Your camera + mic will broadcast to viewers in real-time on Plyx.
          </p>

          <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mt-8 mb-2">
            Stream title
          </label>
          <input
            data-testid="plyx-live-title-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Sunday Afrobeats DJ set"
            maxLength={120}
            className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder-white/30 focus:outline-none focus:border-[#FFCC2E]"
          />
          <p className="text-right text-white/30 text-xs mt-1">{title.length}/120</p>

          <button
            data-testid="plyx-live-start-btn"
            disabled={starting || !title.trim()}
            onClick={handleGoLive}
            className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-bold text-black bg-gradient-to-r from-[#FF3D7F] to-[#FFCC2E] hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {starting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Radio className="w-5 h-5" /> Start broadcasting
              </>
            )}
          </button>

          <div className="mt-8 rounded-2xl border border-white/5 bg-white/[0.02] p-4 text-xs text-white/40 leading-relaxed">
            Your browser will ask for camera + mic permission. Plyx Live uses
            LiveKit WebRTC — low latency, no third-party watermark, broadcast
            ends when you tap End.
          </div>
        </div>
      ) : (
        <div className="fixed inset-0 z-50 bg-black flex flex-col" data-lk-theme="default">
          <div className="px-4 py-3 flex items-center justify-between bg-black/80 backdrop-blur border-b border-white/10">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-black tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
              </span>
              <p className="font-bold text-sm truncate max-w-[40vw]">{title}</p>
            </div>
            <div className="flex items-center gap-1.5">
              {/* Copy link — paste into WhatsApp/SMS to bring your crew in */}
              <button
                type="button"
                data-testid="plyx-live-copy-link"
                onClick={async () => {
                  const url = `${window.location.origin}/plyx/live/${session.room_name}`;
                  try {
                    await navigator.clipboard.writeText(url);
                    toast.success("Live link copied — paste it anywhere");
                  } catch {
                    toast.message(url, { duration: 8000 });
                  }
                }}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-white/15 hover:bg-white/25 text-white"
                title="Copy live link"
              >
                <Copy className="w-3.5 h-3.5" /> Copy link
              </button>
              {/* One-tap share — uses Web Share API where available
                  (mobile), falls back to a WhatsApp deep-link so the
                  user can fire the URL into their crew without leaving
                  the broadcast screen. */}
              <button
                type="button"
                data-testid="plyx-live-share-btn"
                onClick={async () => {
                  const url = `${window.location.origin}/plyx/live/${session.room_name}`;
                  const text = `I'm live on Plyx right now → ${url}`;
                  if (navigator.share) {
                    try {
                      await navigator.share({ title: "I'm live on Plyx", text, url });
                      return;
                    } catch { /* user cancelled — fall through */ }
                  }
                  window.open(
                    `https://wa.me/?text=${encodeURIComponent(text)}`,
                    "_blank", "noopener",
                  );
                }}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-[#25D366] to-[#1ebe57] text-white"
                title="Share live link"
              >
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
              <button
                data-testid="plyx-live-end-btn"
                onClick={handleEnd}
                disabled={ending}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-white text-black hover:bg-white/90 disabled:opacity-50"
              >
                <X className="w-4 h-4" /> End
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0">
            <LiveKitRoom
              token={session.token}
              serverUrl={session.livekit_url}
              connect={true}
              video={true}
              audio={true}
              // ── Video quality (broadcaster side) ────────────────────────
              // Default LiveKit capture is 640×360 — too soft for streams
              // viewers watch full-screen on phones. Force 720p capture
              // with simulcast layers so weak-connection viewers get an
              // adaptive 360p / 540p fallback while strong connections
              // get the full 720p source. Bumps perceived quality without
              // hurting low-end viewers.
              videoCaptureDefaults={{
                resolution: VideoPresets.h720.resolution,
                facingMode: "user",
              }}
              publishDefaults={{
                videoSimulcastLayers: [VideoPresets.h360, VideoPresets.h540],
                videoEncoding: VideoPresets.h720.encoding,
                videoCodec: "vp9",
                dtx: true,
                red: true,
              }}
              audioCaptureDefaults={{
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
              }}
              data-lk-theme="default"
              style={{ height: "100%" }}
              onDisconnected={() => {
                // If LiveKit drops the session unexpectedly, surface gracefully.
                toast.message("Disconnected from LiveKit");
              }}
            >
              <div className="h-full flex flex-col relative">
                {/* Plays every subscribed audio track (the host's own mic
                    is excluded by LiveKit). Without this the host couldn't
                    hear guests they brought onto the stage. */}
                <RoomAudioRenderer />
                <div className="flex-1 min-h-0 relative">
                  <BroadcasterStage roomName={session.room_name} />
                  <BroadcasterControls />
                  <HostGiftLauncher roomName={session.room_name} />
                  <BroadcasterEngagement roomName={session.room_name} />
                  {/* Stage controls — host gets the full panel (raise-
                      hand queue, pending invites, mute/remove buttons,
                      invite-by-handle search). */}
                  <PlyxStagePanel
                    roomName={session.room_name}
                    hostId={user?.plyx_user_id}
                    isBroadcaster={true}
                  />
                  {/* iter-119o: Bumper Challenge — live gift battle.
                      Host gets the "Start Bumper" CTA + picker; once
                      active, the standings tile renders top-right and
                      the "End battle" button surfaces in-tile. */}
                  <BumperBattlePanel
                    roomName={session.room_name}
                    isHost={true}
                    hostId={user?.plyx_user_id}
                  />
                </div>
                <div className="bg-black/90 border-t border-white/10 p-2">
                  <ControlBar variation="minimal" controls={{ camera: true, microphone: true, screenShare: false, chat: false, leave: false }} />
                </div>
              </div>
            </LiveKitRoom>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlyxLiveNew;
